// TaskSection.tsx
import React, { useCallback, useEffect, useState } from 'react';
import TaskIconRenderer from '../Utility/TaskIconRenderer';
import { PiX } from 'react-icons/pi';
import { MdDragIndicator } from 'react-icons/md';
import add_task_icon from '../../../assets/icons/add_task.svg';
import startendbar from '../../../assets/icons/editclienttask.svg';
import { IconTrash } from '@tabler/icons-react';
import AddTaskDialog, {
  Task,
} from '../../../reusable/Client/dialog/AddTaskDialog';
import axiosInstance from 'config/axiosInstance';
import RemoveTaskDialog from '../../../reusable/Client/dialog/RemoveTaskDialog';
import { useTranslation } from 'react-i18next';

interface Props {
  selectedTasks: Task[];
  setSelectedTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  isRemoveTask?: boolean;
  showErrorTooltip: boolean;
  setShowErrorTooltip: (errorTooltip: boolean) => void;
}

const TaskAssignment: React.FC<Props> = ({
  selectedTasks,
  setSelectedTasks,
  isRemoveTask,
  showErrorTooltip,
  setShowErrorTooltip,
}) => {
  const { t } = useTranslation('client');
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isRemoveTaskModalOpen, setIsRemoveTaskModalOpen] = useState(false);
  const [selectedTaskIdForRemoval, setSelectedTaskIdForRemoval] = useState<
    number | null
  >(null);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);

  // Fetch tasks from the API
  const fetchTasks = useCallback(async () => {
    try {
      const tasksResponse = await axiosInstance.get(`/v1/tasks/?page_size=15`);
      const taskData = tasksResponse.data.results || tasksResponse.data;
      setAllTasks(Array.isArray(taskData) ? taskData : []);
    } catch (error) {
      console.error('Failed to fetch tasks', error);
      setAllTasks([]);
    }
  }, []);

  // Fetch tasks when component mounts
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Handle task addition
  const handleAddTasks = (newTaskIds: number[]) => {
    setSelectedTasks((prevSelectedTasks) => {
      const existingTaskIds = new Set(prevSelectedTasks.map((task) => task.id));

      // Keep tasks that are already selected and are part of the newTaskIds
      const tasksToKeep = prevSelectedTasks.filter((task) =>
        newTaskIds.includes(task.id)
      );

      // Add new tasks from the newTaskIds that are not already selected
      const newTasks = newTaskIds
        .filter((taskId) => !existingTaskIds.has(taskId))
        .map((taskId) => {
          const task = allTasks.find((t) => t.id === taskId);
          return task ? { ...task, isDisabled: false } : null;
        })
        .filter((task): task is Task => task !== null); // Ensure the result is a Task[]

      // Combine existing tasks to keep and new tasks
      return [...tasksToKeep, ...newTasks];
    });

    setIsTaskModalOpen(false);
  };

  // Handle task removal
  const handleRemoveTask = () => {
    if (selectedTaskIdForRemoval !== null) {
      setSelectedTasks((prevSelected) =>
        prevSelected.filter((task) => task.id !== selectedTaskIdForRemoval)
      );
    }
    setIsRemoveTaskModalOpen(false);
  };

  // Handle dragging of tasks
  const onDragStart = (index: number) => {
    setDraggedTaskIndex(index);
  };

  const onDragEnter = (index: number) => {
    if (draggedTaskIndex !== null && draggedTaskIndex !== index) {
      const updatedTasks = [...selectedTasks];
      const [draggedTask] = updatedTasks.splice(draggedTaskIndex, 1);
      updatedTasks.splice(index, 0, draggedTask);
      setSelectedTasks(updatedTasks);
      setDraggedTaskIndex(index);
    }
  };

  const onDragEnd = () => {
    setDraggedTaskIndex(null);
  };

  return (
    <>
      <div className="flex flex-col px-6 py-4 gap-4">
        <div className="flex flex-row justify-between mb-4">
          <div className="relative">
            <h6
              className="font-medium text-subtitle-1 relative inline-block cursor-pointer"
              onMouseEnter={() => setShowErrorTooltip(true)}
              onMouseLeave={() => setShowErrorTooltip(false)}
            >
              {t('detail.tasks')} <span className="text-error">*</span>
            </h6>
            {showErrorTooltip && (
              <div className="absolute bg-red-100 text-red-600 border border-red-300 p-2 rounded mt-1 z-10 whitespace-nowrap">
                {t('manage_client.add_task_required')}
              </div>
            )}
          </div>
          <div className="flex flex-row gap-6 text-primary/300 items-center">
            {!isRemoveMode ? (
              <>
                <button
                  type="button"
                  className="h-8 bg-primary/50 text-primary/300 px-5 rounded-md text-caption"
                  onClick={() => setIsTaskModalOpen(true)}
                >
                  + {t('manage_client.add_task')}
                </button>
                {isRemoveTask && (
                  <button
                    type="button"
                    onClick={() => setIsRemoveMode(true)} // Activate remove mode
                    className="h-8 flex flex-row gap-1 items-center text-primary/300 rounded-md text-caption"
                  >
                    <IconTrash stroke={1.5} size={16} />
                    {t('manage_client.remove_task')}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsRemoveMode(false)} // Deactivate remove mode
                className="h-8 text-primary/300 rounded-md text-caption"
              >
                {t('action.cancel')}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-2 w-full justify-between items-center mb-2">
          <p className="flex-shrink-0 text-primary/75">START</p>
          <img src={startendbar} alt="Start to End" className="flex-grow" />
          <p className="flex-shrink-0 text-primary/75">END</p>
        </div>
        <div className="flex flex-row overflow-x-auto gap-4 pb-4">
          {selectedTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center w-full h-36 gap-3">
              <img src={add_task_icon} alt="Add Task" className="h-12" />
              <p className="text-subtitle-2 font-medium">
                {t('manage_client.add_task_title')}
              </p>
              <p className="text-caption text-neutral/100">
                {t('manage_client.add_task_subtitle')}
              </p>
            </div>
          ) : (
            selectedTasks.map((task, index) => (
              <div
                key={task.id}
                className="relative flex flex-col items-center border-r last:border-none pt-2 pb-6 cursor-move gap-4"
                draggable
                onDragStart={() => onDragStart(index)}
                onDragEnter={() => onDragEnter(index)}
                onDragEnd={onDragEnd}
              >
                <div className="w-40 flex flex-col justify-center items-center">
                  <TaskIconRenderer iconName={task.icon} />
                  <p className="truncate">{task.name}</p>
                </div>

                {!isRemoveMode ? (
                  <MdDragIndicator
                    size={24}
                    className="transform rotate-90 text-neutral/100"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTaskIdForRemoval(task.id);
                      setIsRemoveTaskModalOpen(true);
                    }}
                  >
                    <PiX size={24} className="text-neutral/100" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Task Selection Modal */}
      {isTaskModalOpen && (
        <AddTaskDialog
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          tasks={selectedTasks}
          onAddTasks={handleAddTasks}
        />
      )}

      {isRemoveTaskModalOpen && (
        <RemoveTaskDialog
          clientName="ABC"
          isOpen={isRemoveTaskModalOpen}
          onClose={() => setIsRemoveTaskModalOpen(false)}
          onDelete={handleRemoveTask}
          taskId={selectedTaskIdForRemoval}
        />
      )}
    </>
  );
};

export default TaskAssignment;
